import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler";
import classNames from "classnames";
import { Building2, Check, ChevronDown, ChevronUp } from "lucide-react";
import styles from "./OrgSwitcher.module.scss";
import { useTranslation } from "react-i18next";
import { getOrganizationData } from "../../actions/organization";
import { setLoadingContext } from "../../redux/loaderStore";
import { PAGE_LOADER } from "../../constants/loader";

const OrgSwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId: activeOrgId } = useParams();
  const { t } = useTranslation();
  const organizations = useSelector((state) => state.user.organizations);
  const sessionToken = localStorage.getItem("session_token");

  const [isOpen, setIsOpen] = useState(false);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const dropdownRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const orgOptions = useMemo(() => {
    return Object.values(organizations || {})
      .map((org) => ({
        id: org?.UUID || org?.id,
        name: org?.Name || org?.name,
      }))
      .filter((org) => org.id && org.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  const hasOptions = orgOptions.length > 0;

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const updateScrollState = () => {
    const el = dropdownRef.current;
    if (!el) return;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    const isScrollable = el.scrollHeight > el.clientHeight + 1;
    setCanScrollMore(isScrollable && !atBottom);
  };

  const startAutoScroll = () => {
    const el = dropdownRef.current;
    if (!el || !canScrollMore) return;
    stopAutoScroll();
    scrollIntervalRef.current = window.setInterval(() => {
      if (!dropdownRef.current) {
        stopAutoScroll();
        return;
      }
      const el = dropdownRef.current;
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if (atBottom) {
        stopAutoScroll();
        updateScrollState();
        return;
      }
      el.scrollBy({ top: 14, behavior: "auto" });
      updateScrollState();
    }, 60);
  };

  useEffect(() => {
    if (!isOpen) {
      stopAutoScroll();
      return;
    }
    updateScrollState();
    const el = dropdownRef.current;
    if (!el) return;
    const handleScroll = () => updateScrollState();
    el.addEventListener("scroll", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      stopAutoScroll();
    };
  }, [isOpen]);

  if (!hasOptions) {
    return null;
  }

  const activeOrg = orgOptions.find((org) => org.id === activeOrgId);
  const dropdownId = "org-switcher-dropdown";

  const handleSelect = (id) => {
    setIsOpen(false);
    if (id === activeOrgId) return;
    dispatch(
      setLoadingContext({
        context: PAGE_LOADER,
        isLoading: true,
      }),
    );
    dispatch(getOrganizationData(id, sessionToken, navigate, true)).finally(
      () =>
        dispatch(
          setLoadingContext({
            context: PAGE_LOADER,
            isLoading: false,
          }),
        ),
    );
  };

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className={styles.switcher}>
        <button
          type="button"
          className={classNames(styles.trigger, { [styles.open]: isOpen })}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={t("organizations.selectPlaceholder")}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? dropdownId : undefined}
        >
          <Building2 className={styles.leadingIcon} size={18} />
          <span
            className={classNames(styles.label, {
              [styles.placeholder]: !activeOrg,
            })}
          >
            {activeOrg?.name || t("organizations.selectPlaceholder")}
          </span>
          {isOpen ? (
            <ChevronUp className={styles.chevron} size={16} />
          ) : (
            <ChevronDown className={styles.chevron} size={16} />
          )}
        </button>
        {isOpen && (
          <div
            id={dropdownId}
            className={styles.dropdown}
            role="listbox"
            aria-activedescendant={activeOrgId || undefined}
            ref={dropdownRef}
          >
            {orgOptions.map((org) => (
              <button
                key={org.id}
                type="button"
                className={classNames(styles.option, {
                  [styles.selected]: org.id === activeOrgId,
                })}
                onClick={() => handleSelect(org.id)}
                role="option"
                aria-selected={org.id === activeOrgId}
              >
                <span className={styles.optionLabel}>{org.name}</span>
                <Check className={styles.check} size={16} />
              </button>
            ))}
            {canScrollMore && (
              <div
                className={styles.scrollHint}
                onMouseEnter={startAutoScroll}
                onMouseLeave={stopAutoScroll}
              >
                <ChevronDown size={14} />
              </div>
            )}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default OrgSwitcher;
